import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, functions } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useDemoMode } from '../context/DemoContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 9999,
  background: 'rgba(0,0,0,0.65)',
  backdropFilter: 'blur(6px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
};

const modalStyle = {
  position: 'relative',
  background: 'var(--bg-card)',
  borderRadius: '16px',
  border: '1px solid var(--border)',
  padding: '36px 32px 28px',
  width: '100%',
  maxWidth: '480px',
  animation: 'popIn 0.28s ease',
};

const closeStyle = {
  position: 'absolute', top: '14px', right: '16px',
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-secondary)', fontSize: '22px', lineHeight: 1,
};

const badgeStyle = {
  display: 'inline-block',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: '11px', fontWeight: 700,
  letterSpacing: '1px',
  padding: '3px 10px',
  borderRadius: '20px',
  marginBottom: '14px',
};

const headingStyle = {
  color: 'var(--text-primary)',
  fontSize: '22px', fontWeight: 800,
  lineHeight: 1.3,
  marginBottom: '10px',
};

const subtextStyle = {
  color: 'var(--text-secondary)',
  fontSize: '14px', lineHeight: 1.6,
  marginBottom: '16px',
};

const proofStyle = {
  fontSize: '13px',
  color: 'var(--text-secondary)',
  marginBottom: '18px',
};

const rowStyle = {
  display: 'flex', gap: '8px', marginBottom: '12px',
};

const inputStyle = {
  flex: 1,
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid var(--border)',
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  fontSize: '14px',
  outline: 'none',
};

const btnStyle = {
  padding: '10px 18px',
  borderRadius: '8px',
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  fontWeight: 700,
  fontSize: '14px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const disclaimerStyle = {
  fontSize: '12px',
  color: 'var(--text-secondary)',
  textAlign: 'center',
  marginBottom: '14px',
};

const skipStyle = {
  display: 'block',
  textAlign: 'center',
  fontSize: '13px',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  textDecoration: 'underline',
  width: '100%',
};

const keyframes = `@keyframes popIn {
  from { transform: scale(0.85); opacity: 0; }
  to   { transform: scale(1);    opacity: 1; }
}`;

export default function ExitIntentPopup({ onClose, onEmailSubmitted }) {
  const navigate = useNavigate();
  const { isDemoMode } = useDemoMode();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!EMAIL_RE.test(email)) {
      setError('Valid email daalo.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await httpsCallable(functions, 'addBrevoContact')({ email });

      await addDoc(collection(db, 'ebookLeads'), {
        email,
        capturedAt: serverTimestamp(),
        source: window.location.pathname,
      });

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'ebook_lead_captured');
      }

      setSuccess(true);
      if (onEmailSubmitted) onEmailSubmitted();
    } catch {
      setError('Kuch gadbad ho gaya, dobara try karo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <style>{keyframes}</style>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <button style={closeStyle} onClick={onClose} aria-label="Close">×</button>

        {success ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎉</div>
            <div style={{ ...headingStyle, fontSize: '20px' }}>Done! Ebook bhej diya</div>
            <p style={subtextStyle}>Apna inbox check karo — PDF aa gaya hoga.</p>
            <a
              href="/signup"
              style={{ ...btnStyle, display: 'inline-block', textDecoration: 'none', marginTop: '8px' }}
            >
              Free Account Banao →
            </a>
          </div>
        ) : isDemoMode ? (
          <>
            <div style={badgeStyle}>DEMO MODE</div>
            <div style={headingStyle}>Enjoying the Demo? 🎯</div>
            <p style={subtextStyle}>
              Sign up free and start tracking your real trades today!
            </p>
            <button
              onClick={() => {
                if (typeof window.gtag === 'function') {
                  window.gtag('event', 'demo_to_signup', { event_category: 'demo', event_label: 'exit_popup' });
                }
                onClose();
                navigate('/signup');
              }}
              style={{ ...btnStyle, width: '100%', padding: '12px', fontSize: '15px', marginBottom: '12px' }}
            >
              Create Free Account →
            </button>
            <button style={skipStyle} onClick={onClose}>
              Continue exploring demo →
            </button>
          </>
        ) : (
          <>
            <div style={badgeStyle}>FREE EBOOK</div>
            <div style={headingStyle}>Ruko! Ek kaam ki cheez free mein lo 👇</div>
            <p style={subtextStyle}>
              7 Trading Mistakes Jo Aapko Barbad Kar Rahi Hain — PDF guide bilkul free, seedha inbox mein.
            </p>
            <p style={proofStyle}>🟢 2,300+ traders ne already download kiya</p>

            <div style={rowStyle}>
              <input
                type="email"
                placeholder="apna@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                style={inputStyle}
              />
              <button onClick={handleSubmit} disabled={loading} style={btnStyle}>
                {loading ? '...' : 'Send Karo'}
              </button>
            </div>

            {error && (
              <p style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '8px' }}>{error}</p>
            )}

            <p style={disclaimerStyle}>No spam. Kabhi nahi. Unsubscribe anytime.</p>
            <button style={skipStyle} onClick={onClose}>
              Nahi chahiye, main chala jaata hoon →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
